import { RightOutlined } from '@ant-design/icons'
import type { Address } from '@lmc/ts-common'
import { Avatar, Heading } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfoIcons from 'screens/Rostering/RotaBuilder/common/components/InfoIcons'
import { getNewAddress } from 'screens/Rostering/RotaPattern/utils'
import type { MonitoringEvent } from 'screens/Rostering/types'
import {
  DAY_FIRST_DATE_FORMAT,
  getDateTimeInZone,
  TIME_FORMAT
} from 'screens/Rostering/utils.datetime'
import styled from 'styled-components'
import MonitoringRotaDrawer from './MonitoringRotaDrawer'

type EventDetailsProps = {
  event?: MonitoringEvent
}

const EventDetails = ({ event }: EventDetailsProps) => {
  const { t } = useTranslation()

  const [drawerVisible, setDrawerVisible] = useState(false)

  if (!event) return null

  const { resident } = event

  if (!event.siteShift && !resident) return null

  const isOvernight =
    getDateTimeInZone(event.startTime).date() <
    getDateTimeInZone(event.endTime).date()

  return (
    <Container>
      <HeaderRow>
        {event.siteShift ? (
          <Heading level={4}>{event.title}</Heading>
        ) : resident ? (
          <ResidentSection>
            <Resident>
              <Avatar size={36} url={resident.picture} />
              <Heading level={4}>
                {`${resident.name?.first} ${resident.name?.last}`}
              </Heading>
            </Resident>
            <InfoIcons
              address={
                resident.address
                  ? getNewAddress(resident.address as unknown as Address)
                  : undefined
              }
              email={resident.email}
              phone={resident.phoneNumber}
              notes={resident.carePackage?.visitNotes}
              visit
            />
          </ResidentSection>
        ) : null}
        <DetailsSection onClick={() => setDrawerVisible(true)}>
          <Text weight="medium">{t('EVENT_DETAILS')}</Text>
          <RightOutlined />
        </DetailsSection>
      </HeaderRow>
      <DetailsRow>
        <InfoSection>
          <StyledText level={2}>{t('TIME_WINDOW')}</StyledText>
          <FlexiSection>
            <HoursSection>
              <Text weight="bold">
                {`${getDateTimeInZone(event.startTime).format(
                  TIME_FORMAT
                )} - ${getDateTimeInZone(event.endTime).format(TIME_FORMAT)}`}
              </Text>
              {isOvernight && (
                <OvernightText level={2}>{t('PLUS_ONE')}</OvernightText>
              )}
            </HoursSection>
            {event.flexiType && <Text level={2}>· {t('FLEXI')}</Text>}
          </FlexiSection>
        </InfoSection>
        <InfoSection>
          <StyledText level={2}>{t('PLANNED_DURATION')}</StyledText>
          <Text weight="bold">{event.expectedDuration}</Text>
        </InfoSection>
        <InfoSection>
          <StyledText level={2}>{t('DATE')}</StyledText>
          <Text weight="bold">
            {getDateTimeInZone(event.startTime).format(DAY_FIRST_DATE_FORMAT)}
          </Text>
        </InfoSection>
      </DetailsRow>
      {drawerVisible && (
        <MonitoringRotaDrawer
          onClose={() => setDrawerVisible(false)}
          isVisible={drawerVisible}
          revisionId={event.id}
        />
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background-color: ${(props) => props.theme.color.neutral.color10};
  border-radius: 20px;
`
const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const ResidentSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`
const Resident = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`
const DetailsSection = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  color: ${(props) => props.theme.color.status.primary};

  &:hover {
    cursor: pointer;
  }
`
const DetailsRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
`
const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 170px;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const FlexiSection = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`
const HoursSection = styled.div`
  display: flex;
  gap: 4px;
`
const OvernightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  position: relative;
  top: 0;
  transform: translateY(-10%);
`
export default EventDetails
